import { cn } from "@/lib/utils";
import type {
  BasicPost,
  ContactCard,
  FeedPostables,
  NativeComponent,
  Video,
  WebLinks,
} from "@terrabase/db";
import { memo } from "react";
import { BasicPostPreview } from "./previews/basic-post-preview";
import { EventPreview } from "./previews/event-preview";
import { VideoPreview } from "./previews/video-preview";
import { WebLinkPreview } from "./previews/web-link-preview";
import type {
  CategoryDisplayType,
  PostableWithExtras,
  PreviewProps,
} from "./types";

export type FeedPostable = {
  postableType:
    | "video"
    | "basicPost"
    | "contactCard"
    | "story"
    | "event"
    | "quickLink"
    | "webLink"
    | "nativeScreen";
  postable: Video | BasicPost | ContactCard | NativeComponent | WebLinks;
};

export function isBasicPost(
  post: FeedPostables,
): post is FeedPostables & { postableType: "basicPost" } {
  return post.postableType === "basicPost";
}

// TypeCheck functions
export function isWebLink(
  post: FeedPostables,
): post is FeedPostables & { postableType: "webLink" } {
  return post.postableType === "webLink";
}

export function isEvent(
  post: FeedPostables,
): post is FeedPostables & { postableType: "event" } {
  return post.postableType === "event";
}

export function isVideo(
  post: FeedPostables,
): post is FeedPostables & { postableType: "video" } {
  return post.postableType === "video";
}

// Memoize the PreviewCanvas component
export const PreviewCanvas = memo(function PreviewCanvas({
  postable,
  displayType,
}: PreviewProps) {
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-full max-w-[360px] rounded-lg overflow-hidden border border-border bg-slate-50 shadow-sm">
        <div className="text-xs text-muted-foreground text-center py-1 border-b border-border bg-background">
          Mobile Feed Preview
        </div>

        <div className="p-4">
          {/* Phone-shaped preview area */}
          <div
            className={cn(
              "overflow-hidden",
              displayType === "vertical" ? "min-h-[400px]" : "min-h-[200px]",
            )}
          >
            {/* Render the right preview based on postable type */}
            {renderPreviewComponent(postable, displayType)}
          </div>
        </div>
      </div>
    </div>
  );
});

function renderPreviewComponent(
  postable: PostableWithExtras,
  displayType: CategoryDisplayType,
) {
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  if (isBasicPost(postable as any)) {
    return <BasicPostPreview postable={postable} displayType={displayType} />;
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  if (isWebLink(postable as any)) {
    return <WebLinkPreview postable={postable} displayType={displayType} />;
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  if (isEvent(postable as any)) {
    return <EventPreview postable={postable} displayType={displayType} />;
  }

  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  if (isVideo(postable as any)) {
    return <VideoPreview postable={postable} displayType={displayType} />;
  }

  // Fallback
  return (
    <div className="flex flex-col items-center justify-center h-full p-6 text-center text-muted-foreground">
      <p>Preview not available for this item type.</p>
      <p className="text-xs mt-1">{postable.postableType}</p>
    </div>
  );
}
